import { createCachedOrderDeleteMutation, deleteLocalOrder, deleteOrder, getOrder, getLocalOrder, setOrderDuplicateResolution } from '@/api/orders';
import { canDeleteOrder, isLocalOrder, isProtectedFromDelete } from '@/utils/offlineDeletePolicy';

// TCA-26101819: target-scoped renderer custody survives screen replacement.
// No credentials, passwords, recipient data, or durable retry instructions.
const claims = new Set();
const observers = new Set();
const same = (a, b) => a.endpoint === b.endpoint && a.tenant === b.tenant && a.id === b.id;
const emit = () => observers.forEach(fn => fn());
export const warningIdentity = order => JSON.stringify(order?.duplicate_warning ?? null);

const acknowledgement = (value, claim) => {
  if (claim.kind === 'duplicate') return value?.message === 'Resolution updated' && value.resolution === claim.resolution ? 'Marked as ' + (claim.resolution === 'approved' ? 'approved' : 'unaddressed') : null;
  if (claim.kind === 'local') return value?.deleted === true ? 'Local order removed' : null;
  if (claim.kind === 'queued') return value?.success === true && value.order_id === claim.id
    && typeof value.mutation_id === 'string' && !!value.mutation_id.trim() && value.state === 'pending_review' ? 'Deletion queued for Review' : null;
  if (value?.message === 'Order deleted successfully') return 'Order deleted';
  return value?.message === 'Order already deleted' ? 'Order already absent' : null;
};
const refusal = error => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (status === 403 && detail === 'Incorrect password') return 'Incorrect password';
  if ([401, 403].includes(status)) return 'Permission unavailable. Sign in and review current access.';
  if (status === 409 && ['Duplicate warning changed. Refresh and review it again.',
    'This record changed before your update completed. Refresh and try again.',
    'Cached order already has an offline change pending'].includes(detail)) return 'The order changed or has pending work. Refresh and review it again.';
  if (status === 404 && ['No duplicate warning on this order', 'Unsynchronized local order not found', 'Cached cloud order not found'].includes(detail)) return 'This target is no longer available. Refresh and review.';
  if (status === 409 && typeof detail === 'string' && /^Password confirmation required to delete (on route|delivered) order$/.test(detail)) return 'Password confirmation is required. Review the current order.';
  return null;
};

export function createAllOrdersDecisions(capture, read, { queueCloudDeletes = true } = {}) {
  let active = false;
  let authorityDenied = false;
  let snapshot = [];
  const listeners = new Set();
  // Review evidence is private to this owner, not inherited across sessions.
  const reviews = new Map();
  const related = claim => [capture(false), capture(true)].some(owner => owner && owner.endpoint === claim.endpoint && owner.tenant === claim.tenant);
  const notify = () => {
    snapshot = [...claims].filter(related).map(claim => ({ id: claim.id, kind: claim.kind, state: claim.state,
      stalled: claim.stalled, review: reviews.get(claim)?.message, retry: reviews.get(claim)?.retry === true,
      complete: reviews.get(claim)?.complete === true, absent: reviews.get(claim)?.absent === true }));
    listeners.forEach(fn => fn());
  };
  const ownerFor = claim => capture(claim.kind === 'local' || claim.kind === 'queued');
  const find = id => [...claims].find(claim => claim.id === id && related(claim));
  return {
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    getSnapshot: () => snapshot,
    activate() { active = true; observers.add(notify); notify(); },
    retire() { active = false; reviews.clear(); observers.delete(notify); },
    blocked: id => authorityDenied || !!find(id),
    async run(order, action, { password, resolution, current = () => true, onAccepted } = {}) {
      const kind = action === 'duplicate' ? action : isLocalOrder(order) ? 'local' : capture(false)?.offline ? 'queued' : 'delete';
      if (!queueCloudDeletes && kind === 'queued') return { state: 'blocked' };
      const owner = capture(kind === 'local' || kind === 'queued');
      if (!active || authorityDenied || !owner?.admin || !current() || find(order.id)) return { state: 'blocked' };
      if (kind === 'duplicate' ? owner.offline || isLocalOrder(order) || !order.duplicate_warning
        || !['approved', 'unaddressed'].includes(resolution)
        : !canDeleteOrder(order, { isAdmin: owner.admin, offline: owner.offline })
          || (isProtectedFromDelete(order) && !password)) return { state: 'blocked' };
      const claim = { endpoint: owner.endpoint, tenant: owner.tenant, id: order.id, kind, resolution, state: 'pending' };
      if ([...claims].some(other => same(other, claim))) return { state: 'blocked' };
      claims.add(claim); emit();
      const owns = () => active && owner.current() && current();
      if (!owns()) { claims.delete(claim); emit(); return { state: 'retired' }; }
      const timer = setTimeout(() => { claim.stalled = true; emit(); }, 30000);
      let message; let refused;
      try {
        const result = kind === 'duplicate' ? await setOrderDuplicateResolution(order.id, resolution, owner.config)
          : kind === 'local' ? await deleteLocalOrder(order.id, owner.config)
            : kind === 'queued' ? await createCachedOrderDeleteMutation(order.id, owner.config)
              : await deleteOrder(order.id, password, owner.config);
        message = acknowledgement(result, claim);
      } catch (error) {
        refused = refusal(error);
        if (owner.current() && [401, 403].includes(error?.response?.status)
          && error?.response?.data?.detail !== 'Incorrect password') authorityDenied = true;
      }
      finally {
        clearTimeout(timer);
        // Original-target settlement is independent of current screen feedback.
        if (message) onAccepted?.();
        if (message || refused) claims.delete(claim);
        else claim.state = 'unknown';
        emit();
      }
      if (!owns()) return { state: 'retired' };
      return { state: message ? 'accepted' : refused ? 'refused' : 'unknown', message: message || refused || 'Result not confirmed. Check this order before trying again.' };
    },
    async review(id) {
      const claim = find(id);
      if (!active || !claim || claim.state !== 'unknown') return;
      const owner = ownerFor(claim);
      if (!owner?.admin || authorityDenied) return;
      const attempt = {};
      reviews.set(claim, attempt); notify();
      const owns = () => active && owner.current() && claims.has(claim) && reviews.get(claim) === attempt;
      try {
        const item = await read(config => {
          if (!owns()) throw new Error('Observation retired before dispatch');
          return (claim.kind === 'local' || claim.kind === 'queued' ? getLocalOrder : getOrder)(id, config);
        }, owner.config);
        if (!owns()) return;
        if (item?.id !== id) throw new Error('Invalid observation');
        const pending = item.desktop_sync?.pending_delete === true;
        const achieved = pending || (claim.kind === 'duplicate' && item.duplicate_warning?.resolution === claim.resolution);
        const eligible = claim.kind === 'duplicate' ? !!item.duplicate_warning && !owner.offline && !isLocalOrder(item)
          : canDeleteOrder(item, { isAdmin: owner.admin, offline: owner.offline }) && (isLocalOrder(item) === (claim.kind === 'local'))
            && !item.desktop_sync?.pending_mutation;
        reviews.set(claim, { current: owner.current, retry: !achieved && eligible,
          message: pending ? 'A deletion is queued. Continue in Review; do not submit another request.'
            : achieved ? 'The requested resolution is now present. This does not identify which attempt saved it.'
              : eligible ? 'Current order observed. A new attempt requires your acknowledgement below.' : 'Current order is not eligible. Review its pending work or permissions.',
          complete: achieved });
      } catch (error) {
        if (!owns()) return;
        const absent = error?.response?.status === 404 && error.response.data?.detail === 'Order not found';
        reviews.set(claim, { current: owner.current, complete: absent, absent, message: absent ? 'Order currently absent. This does not prove which attempt removed it.' : 'Current order could not be verified. No retry has been authorized.' });
      }
      notify();
    },
    acknowledge(id, onAcknowledged) {
      const claim = find(id); const evidence = reviews.get(claim); const owner = claim && ownerFor(claim);
      if (!active || authorityDenied || !owner?.admin || claim.state !== 'unknown' || !evidence?.current?.() || !(evidence?.retry || evidence?.complete)) return false;
      onAcknowledged?.(evidence, claim);
      claims.delete(claim); reviews.delete(claim); emit();
      return true;
    },
  };
}
